import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import MatchBar from "../components/jobsiq/MatchBar";
import ResumeParseDialog from "../components/jobsiq/ResumeParseDialog";
import {
    Briefcase,
    ChatCircleDots,
    VideoCamera,
    Upload,
    FileText,
    User as UserIcon,
    ArrowRight,
    Buildings,
    Users,
    ChartBar,
    Plus,
    Share,
    Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import VerifiedBadge from "../components/jobsiq/VerifiedBadge";

function ShareReportButton({ application, onShared }) {
    const [busy, setBusy] = useState(false);
    const share = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/applications/${application.application_id}/share`);
            const url = `${window.location.origin}/r/${data.share_token}`;
            await navigator.clipboard.writeText(url).catch(() => {});
            toast.success("Public link copied to clipboard");
            onShared?.(data.share_token);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create link");
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={share}
            disabled={busy}
            data-testid={`share-report-${application.application_id}`}
            className="text-xs px-2 py-1 border border-slate-300 hover:bg-blue-600 hover:text-white hover:border-blue-600 inline-flex items-center gap-1"
        >
            <Share size={14} /> {application.share_token ? "Copy link" : "Share report"}
        </button>
    );
}

function SeekerView() {
    const { user, refresh, setUser } = useAuth();
    const [stats, setStats] = useState(null);
    const [applications, setApplications] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [parseResult, setParseResult] = useState(null);

    const loadApps = async () => {
        const a = await api.get("/applications/mine");
        setApplications(a.data);
    };

    useEffect(() => {
        (async () => {
            const [s, a] = await Promise.all([api.get("/stats"), api.get("/applications/mine")]);
            setStats(s.data);
            setApplications(a.data);
        })();
    }, []);

    const uploadResume = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post("/profile/parse-resume", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await refresh();
            setParseResult(data);
            toast.success("Résumé uploaded — review what we found");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const applyParsed = async (fields) => {
        try {
            const { data } = await api.put("/auth/profile", fields);
            setUser(data);
            setParseResult(null);
            toast.success("Profile updated from résumé");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Apply failed");
        }
    };

    return (
        <>
            <section className="border-b border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-10">
                    <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                        Seeker · Command center
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="font-display text-4xl">Hello, {user?.name}.</h1>
                        {user?.is_verified && <VerifiedBadge size="lg" tone="light" testId="dashboard-verified-badge" />}
                    </div>
                    <p className="text-slate-600 mt-2">
                        Your match scores, applications and interview intelligence — all in one view.
                    </p>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 py-8 grid md:grid-cols-4 gap-px bg-slate-200">
                {[
                    { label: "Applications", v: stats?.applications ?? 0, icon: Briefcase },
                    { label: "Avg. match", v: (stats?.avg_match ?? 0) + "/100", icon: ChartBar },
                    { label: "Shortlisted", v: stats?.by_status?.shortlisted ?? 0, icon: UserIcon },
                    { label: "Interviews ready", v: stats?.by_status?.reviewing ?? 0, icon: VideoCamera },
                ].map((s) => (
                    <div key={s.label} className="bg-white p-6">
                        <s.icon size={18} className="text-slate-400" />
                        <div className="font-display text-3xl mt-3">{s.v}</div>
                        <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 mt-1">
                            {s.label}
                        </div>
                    </div>
                ))}
            </section>

            <section className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-2xl">My applications</h3>
                        <Link data-testid="browse-jobs-link" to="/jobs" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                            Browse more jobs <ArrowRight size={14} />
                        </Link>
                    </div>
                    {applications.length === 0 && (
                        <div className="mt-6 border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">
                            No applications yet. <Link to="/jobs" className="text-blue-600">Find your next role →</Link>
                        </div>
                    )}
                    <div className="mt-4 divide-y divide-slate-200">
                        {applications.map((a) => (
                            <div key={a.application_id} className="py-4 flex items-start justify-between gap-4" data-testid={`application-row-${a.application_id}`}>
                                <div>
                                    <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                                        {a.company_name}
                                    </div>
                                    <div className="font-display text-lg">{a.job_title}</div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        Status:{" "}
                                        <span className="uppercase font-mono-plex text-slate-950">{a.status}</span>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <MatchBar score={a.match_score} />
                                    <div className="flex gap-1.5">
                                        <Link
                                            to={`/video-interview/${a.application_id}`}
                                            className="text-xs px-2 py-1 border border-slate-300 hover:bg-slate-950 hover:text-white"
                                            data-testid={`record-video-${a.application_id}`}
                                        >
                                            {a.video_path ? "View" : "Record"}
                                        </Link>
                                        <ShareReportButton application={a} onShared={loadApps} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="border border-slate-200 p-6">
                        <h3 className="font-display text-xl inline-flex items-center gap-1.5">
                            Résumé <Sparkle size={14} weight="fill" className="text-blue-600" />
                        </h3>
                        <p className="text-sm text-slate-600 mt-2">
                            Upload a PDF — we'll auto-fill your skills, title and bio.
                        </p>
                        <div className="mt-4">
                            {user?.resume_path ? (
                                <div className="text-sm border border-slate-200 p-3 bg-slate-50 flex items-center gap-2">
                                    <FileText size={18} /> Résumé on file
                                </div>
                            ) : (
                                <div className="text-sm text-slate-500">No résumé uploaded yet.</div>
                            )}
                            <label className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-950 hover:text-white cursor-pointer">
                                <Upload size={16} />
                                {uploading ? "Parsing…" : user?.resume_path ? "Replace & re-parse" : "Upload & parse résumé"}
                                <input data-testid="resume-upload-input" hidden type="file" accept=".pdf,application/pdf" onChange={uploadResume} disabled={uploading} />
                            </label>
                        </div>
                    </div>

                    <Link
                        to="/chatbot"
                        data-testid="seeker-chatbot-cta"
                        className="block border border-slate-200 p-6 hover:border-slate-950"
                    >
                        <ChatCircleDots size={22} weight="duotone" className="text-blue-600" />
                        <h3 className="font-display text-xl mt-2">AI screening</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Role-specific chatbot or generic skill assessment.
                        </p>
                    </Link>

                    <Link
                        to="/profile"
                        data-testid="seeker-profile-cta"
                        className="block border border-slate-200 p-6 hover:border-slate-950"
                    >
                        <UserIcon size={22} weight="duotone" />
                        <h3 className="font-display text-xl mt-2">Edit profile</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Add skills, experience and a bio to improve matches.
                        </p>
                    </Link>
                </div>
            </section>
            <ResumeParseDialog
                open={!!parseResult}
                parsed={parseResult?.parsed}
                current={parseResult?.current}
                onClose={() => setParseResult(null)}
                onApply={applyParsed}
            />
        </>
    );
}

function CompanyView() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [jobs, setJobs] = useState([]);

    useEffect(() => {
        (async () => {
            const [s, j] = await Promise.all([api.get("/stats"), api.get("/jobs/mine")]);
            setStats(s.data);
            setJobs(j.data);
        })();
    }, []);

    return (
        <>
            <section className="border-b border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-10 flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                            Company · Command center
                        </div>
                        <h1 className="font-display text-4xl">
                            {user?.company_name || user?.name}
                        </h1>
                        <p className="text-slate-600 mt-2">
                            Post roles, review AI-ranked applicants, watch interview analysis.
                        </p>
                    </div>
                    <Link
                        to="/post-job"
                        data-testid="post-job-cta"
                        className="bg-slate-950 text-white px-5 py-3 hover:bg-blue-600 inline-flex items-center gap-2"
                    >
                        <Plus size={18} weight="bold" /> Post a job
                    </Link>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 py-8 grid md:grid-cols-4 gap-px bg-slate-200">
                {[
                    { label: "Open jobs", v: stats?.jobs ?? 0, icon: Briefcase },
                    { label: "Total applicants", v: stats?.applicants ?? 0, icon: Users },
                    { label: "Shortlisted", v: stats?.by_status?.shortlisted ?? 0, icon: UserIcon },
                    { label: "Hired", v: stats?.by_status?.hired ?? 0, icon: Buildings },
                ].map((s) => (
                    <div key={s.label} className="bg-white p-6">
                        <s.icon size={18} className="text-slate-400" />
                        <div className="font-display text-3xl mt-3">{s.v}</div>
                        <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 mt-1">
                            {s.label}
                        </div>
                    </div>
                ))}
            </section>

            <section className="max-w-[1400px] mx-auto px-6">
                <div className="border border-slate-200 p-6">
                    <h3 className="font-display text-2xl">Your job posts</h3>
                    {jobs.length === 0 && (
                        <div className="mt-6 border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">
                            No jobs yet. <Link to="/post-job" className="text-blue-600">Post your first role →</Link>
                        </div>
                    )}
                    <div className="mt-4 divide-y divide-slate-200">
                        {jobs.map((j) => (
                            <div key={j.job_id} className="py-4 flex items-center justify-between gap-4" data-testid={`company-job-row-${j.job_id}`}>
                                <div>
                                    <div className="font-display text-lg">{j.title}</div>
                                    <div className="text-xs text-slate-500">
                                        {j.location} · {j.job_type} ·{" "}
                                        <span className="font-mono-plex">{j.skills?.slice(0, 4).join(", ")}</span>
                                    </div>
                                </div>
                                <Link
                                    to={`/applicants/${j.job_id}`}
                                    data-testid={`view-applicants-${j.job_id}`}
                                    className="text-sm px-3 py-1.5 bg-slate-950 text-white hover:bg-blue-600 inline-flex items-center gap-1.5"
                                >
                                    View applicants <ArrowRight size={14} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    return (
        <div data-testid="dashboard-page">
            <Navbar />
            {user?.role === "company" ? <CompanyView /> : <SeekerView />}
            <Footer />
        </div>
    );
}
